//AggregateResultsModal.tsx
import React from 'react'
import { Modal, Space, Row, Col, Typography, Select, Button, Statistic, Card, Divider, Tabs, Tooltip, Tag, Table, Badge, Progress, Spin } from 'antd'
import { DownloadOutlined, BarChartOutlined, CrownOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { EVAL_WEIGHTS } from './constants'

const { Title, Text } = Typography

interface AggregatedScore {
  model: string
  category: string
  scores: number[]
  average: number
  min: number
  max: number
  count: number
}

interface ModelRanking {
  model: string
  overallScore: number
  categoryBreakdown: Record<string, {
    average: number
    min: number
    max: number
    count: number
  }>
  avgCost: number
  totalTests: number
  costPerPoint: number
  efficiency: number
}

interface TestResultDB {
  id: string
  language: string
  instruction_id: string
  instruction_name: string
  test_name: string
  total_runs: number
}

interface InstructionOption {
  id: string
  name: string
  count: number
}

interface EvalTokenBreakdown {
  inputTokens: number
  cachedTokens: number
  outputTokens: number
  reasoningTokens: number
}

interface EvalCostBreakdown {
  inputCost: number
  cachedCost: number
  outputCost: number
  reasoningCost: number
  totalCost: number
}

interface EvalCostAggregate {
  model: string
  // Translation tokens aggregated across tests for this translation model
  tokenBreakdown: EvalTokenBreakdown
  // Evaluation cost totals (sum across evaluators)
  costBreakdown: EvalCostBreakdown
  evaluators: Record<string, { tokenBreakdown: EvalTokenBreakdown; costBreakdown: EvalCostBreakdown }>
  testCount: number
  // Added: translation cost breakdown computed from tokenBreakdown and translation model rates
  translationCostBreakdown: EvalCostBreakdown
}

interface AggregateResultsModalProps {
  visible: boolean
  onClose: () => void
  dbResults: TestResultDB[]
  languageTotalCount: number
  selectedLanguage: string
  selectedInstructionId: string
  selectedTestName: string
  selectedBatchId: string
  setSelectedLanguage: (lang: string) => void
  setSelectedInstructionId: (instId: string | undefined) => void
  setSelectedTestName: (testName: string | undefined) => void
  setSelectedBatchId: (batchId: string | undefined) => void
  getAvailableInstructionsForLanguage: (lang: string, testName?: string) => InstructionOption[]
  getAvailableTestNamesForLanguage: (lang: string, instructionId?: string) => Array<{ name: string; count: number }>
  getAvailableBatchesForLanguage: (
    lang: string,
    instructionId?: string,
    testName?: string
  ) => Array<{ id: string; name: string; count: number; latestCreatedAt?: string }>
  getAggregatedResults: (language: string, instructionId?: string, testName?: string, batchId?: string) => AggregatedScore[]
  getModelRankings: (language: string, instructionId?: string, testName?: string, batchId?: string) => ModelRanking[]
  downloadAggregateResults: () => void
  downloadAllAggregateResults: () => void
  loadAggregateResults: () => void
  hasLoadedData: boolean
  filtersDirty: boolean
  getScoreColor: (score: number) => string
  getEvaluationCostAggregates: (language: string, instructionId?: string, testName?: string, batchId?: string) => EvalCostAggregate[]
  loadingData: boolean
  loadingInstructionOptions: boolean
  loadingTestNameOptions: boolean
  loadingBatchOptions: boolean
}

const AggregateResultsModal: React.FC<AggregateResultsModalProps> = ({
  visible,
  onClose,
  dbResults,
  languageTotalCount,
  selectedLanguage,
  selectedInstructionId,
  selectedTestName,
  selectedBatchId,
  setSelectedLanguage,
  setSelectedInstructionId,
  setSelectedTestName,
  setSelectedBatchId,
  getAvailableInstructionsForLanguage,
  getAvailableTestNamesForLanguage,
  getAvailableBatchesForLanguage,
  getAggregatedResults,
  getModelRankings,
  downloadAggregateResults,
  downloadAllAggregateResults,
  loadAggregateResults,
  hasLoadedData,
  filtersDirty,
  getScoreColor,
  getEvaluationCostAggregates,
  loadingData,
  loadingInstructionOptions,
  loadingTestNameOptions,
  loadingBatchOptions,
}) => {
  const languages = Array.from(new Set(dbResults.map(r => r.language))).sort()
  const instructions = selectedLanguage
    ? getAvailableInstructionsForLanguage(selectedLanguage, selectedTestName || undefined)
    : []
  const testNames = selectedLanguage
    ? getAvailableTestNamesForLanguage(selectedLanguage, selectedInstructionId || undefined)
    : []
  const batches = selectedLanguage
    ? getAvailableBatchesForLanguage(
      selectedLanguage,
      selectedInstructionId || undefined,
      selectedTestName || undefined
    )
    : []

  return (
    <Modal
      title="Aggregate Results by Language, Instruction, Test Name, and Batch"
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1400}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Row gutter={[16, 16]} align="middle">
          <Col span={4}>
            <Title level={5} style={{ marginBottom: 8 }}>Language</Title>
            <Select
              style={{ width: '100%' }}
              placeholder="Select a language"
              value={selectedLanguage || undefined}
              onChange={value => {
                setSelectedLanguage(value)
                setSelectedInstructionId('')
                setSelectedTestName(undefined)
                setSelectedBatchId(undefined)
              }}
            >
              {languages.map(lang => (
                <Select.Option key={lang} value={lang}>
                  {lang}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Title level={5} style={{ marginBottom: 8 }}>Instruction Set</Title>
            <Select
              style={{ width: '100%' }}
              placeholder="Select instruction (optional)"
              value={selectedInstructionId || undefined}
              onChange={(value) => setSelectedInstructionId(value)}
              disabled={!selectedLanguage}
              loading={loadingInstructionOptions || loadingData}
              allowClear
            >
              {instructions.map(inst => (
                <Select.Option key={inst.id} value={inst.id}>
                  {inst.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Title level={5} style={{ marginBottom: 8 }}>Test Name</Title>
            <Select
              style={{ width: '100%' }}
              placeholder="Select test name (optional)"
              value={selectedTestName || undefined}
              onChange={(value) => setSelectedTestName(value)}
              disabled={!selectedLanguage}
              loading={loadingTestNameOptions || loadingData}
              allowClear
            >
              {testNames.map(option => (
                <Select.Option key={option.name} value={option.name}>
                  {option.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Title level={5} style={{ marginBottom: 8 }}>Batch</Title>
            <Select
              style={{ width: '100%' }}
              placeholder="Select batch (optional)"
              value={selectedBatchId || undefined}
              onChange={(value) => setSelectedBatchId(value)}
              disabled={!selectedLanguage}
              loading={loadingBatchOptions || loadingData}
              allowClear
            >
              {batches.map(option => (
                <Select.Option key={option.id} value={option.id}>
                  {`${option.name} (${option.id.slice(0, 8)}..., ${option.count})`}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Title level={5} style={{ marginBottom: 8 }}>Actions</Title>
            <Space>
              <Button onClick={loadAggregateResults} type="primary" disabled={!selectedLanguage || loadingData}>
                Load Aggregates
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadAggregateResults}
                disabled={!selectedLanguage || !hasLoadedData || loadingData}
              >
                Download Current
              </Button>
              <Button icon={<DownloadOutlined />} onClick={downloadAllAggregateResults} type="primary">
                Download All
              </Button>
              {filtersDirty && selectedLanguage && (
                <Tag color="orange">Filters changed. Click "Load Aggregates".</Tag>
              )}
            </Space>
          </Col>
        </Row>

        {selectedLanguage && (
          <Spin spinning={loadingData} tip="Loading aggregate scores...">
            <>
            {!hasLoadedData && !loadingData && (
              <Card style={{ marginBottom: 16 }}>
                <Text type="secondary">
                  Select filters and click <Text strong>Load Aggregates</Text> to fetch score summaries.
                </Text>
              </Card>
            )}

            {hasLoadedData && (
              <>
            <Row gutter={16}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total Tests"
                    value={languageTotalCount}
                  />
                </Card>
              </Col>
              {(selectedInstructionId || selectedTestName || selectedBatchId) && (
                <Col span={18}>
                  <Card>
                    <Space wrap size={[8, 8]}>
                      {selectedInstructionId && (
                        <span>
                          <Text strong>Selected Instruction: </Text>
                          <Tag color="blue">
                            {instructions.find(inst => inst.id === selectedInstructionId)?.name}
                          </Tag>
                        </span>
                      )}
                      {selectedTestName && (
                        <span>
                          <Text strong>Selected Test Name: </Text>
                          <Tag color="purple">{selectedTestName}</Tag>
                        </span>
                      )}
                      {selectedBatchId && (
                        <span>
                          <Text strong>Selected Batch: </Text>
                          <Tag color="gold">{selectedBatchId.slice(0, 8)}...</Tag>
                        </span>
                      )}
                    </Space>
                  </Card>
                </Col>
              )}
            </Row>

            <Divider />

            <Tabs defaultActiveKey="detailed">
              <Tabs.TabPane
                key="detailed"
                tab={
                  <Space>
                    <BarChartOutlined />
                    Detailed Results
                  </Space>
                }
              >
                <Title level={5}>
                  Aggregated Scores by Model and Category
                  {selectedInstructionId && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      Filtered by Instruction
                    </Tag>
                  )}
                  {selectedTestName && (
                    <Tag color="purple" style={{ marginLeft: 8 }}>
                      Filtered by Test Name
                    </Tag>
                  )}
                  {selectedBatchId && (
                    <Tag color="gold" style={{ marginLeft: 8 }}>
                      Filtered by Batch
                    </Tag>
                  )}
                </Title>

                {(() => {
                  const aggregatedData = getAggregatedResults(
                    selectedLanguage,
                    selectedInstructionId,
                    selectedTestName,
                    selectedBatchId
                  )
                  const modelGroups = aggregatedData.reduce((acc, item) => {
                    const modelBase = item.model.split(' (eval:')[0]
                    if (!acc[modelBase]) acc[modelBase] = []
                    acc[modelBase].push(item)
                    return acc
                  }, {} as Record<string, AggregatedScore[]>)

                  return (
                    <Tabs>
                      {Object.entries(modelGroups).map(([modelBase, scores]) => {
                        const categorySet = new Set<string>()
                        scores.forEach(score => categorySet.add(score.category))
                        const categories = Array.from(categorySet)

                        const getEvalModel = (modelStr: string) => {
                          const m = modelStr.match(/\(eval: (.+)\)/)
                          return m ? m[1] : 'Unknown'
                        }
                        const overallCandidates = scores.filter(s =>
                          s.category.toLowerCase().includes('overall')
                        )
                        const entries = (overallCandidates.length > 0 ? overallCandidates : scores).map(s => {
                          const evalModel = getEvalModel(s.model)
                          const w = (EVAL_WEIGHTS as Record<string, number>)[evalModel] ?? 1
                          const c = s.count || 1
                          return { avg: s.average, weight: w * c }
                        })
                        let overallAvg: number
                        const totalWeight = entries.reduce((acc, e) => acc + e.weight, 0)
                        if (totalWeight > 0) {
                          const weightedSum = entries.reduce((acc, e) => acc + e.avg * e.weight, 0)
                          overallAvg = weightedSum / totalWeight
                        } else {
                          overallAvg = scores.length > 0 ? scores.reduce((acc, s) => acc + s.average, 0) / scores.length : 0
                        }

                        return (
                          <Tabs.TabPane
                            key={modelBase}
                            tab={
                              <span>
                                {modelBase}{' '}
                                {!isNaN(overallAvg) && (
                                  <Tag color={getScoreColor(overallAvg)}>
                                    {overallAvg.toFixed(1)}
                                  </Tag>
                                )}
                              </span>
                            }
                          >
                            <Table
                              dataSource={categories.map(category => {
                                const categoryScores = scores.filter(s => s.category === category)
                                const row: Record<string, any> = { key: category, category }
                                categoryScores.forEach(score => {
                                  const evalModelMatch = score.model.match(/\(eval: (.+)\)/)
                                  const evalModel = evalModelMatch ? evalModelMatch[1] : 'Unknown'
                                  row[evalModel] = {
                                    average: score.average,
                                    min: score.min,
                                    max: score.max,
                                    count: score.count,
                                  }
                                })
                                return row
                              })}
                              columns={[
                                {
                                  title: 'Category',
                                  dataIndex: 'category',
                                  key: 'category',
                                  width: 200,
                                  fixed: 'left',
                                  render: (text: string) => <strong>{text}</strong>,
                                },
                                ...Array.from(
                                  new Set(
                                    scores.map(
                                      s => s.model.match(/\(eval: (.+)\)/)?.[1] ?? 'Unknown'
                                    )
                                  )
                                ).map(evalModel => ({
                                  title: evalModel,
                                  dataIndex: evalModel,
                                  key: evalModel,
                                  align: 'center' as const,
                                  render: (data: any) =>
                                    data ? (
                                      <Tooltip
                                        title={`Min: ${data.min.toFixed(1)}, Max: ${data.max.toFixed(
                                          1
                                        )}, Count: ${data.count}`}
                                      >
                                        <span style={{ color: getScoreColor(data.average) }}>
                                          {data.average.toFixed(1)}
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      '-'
                                    ),
                                })),
                              ]}
                              pagination={false}
                              bordered
                              size="small"
                              scroll={{ x: 'max-content' }}
                            />
                          </Tabs.TabPane>
                        )
                      })}
                    </Tabs>
                  )
                })()}
              </Tabs.TabPane>

              <Tabs.TabPane
                key="eval_costs"
                tab={
                  <Space>
                    Evaluation Costs
                  </Space>
                }
              >
                <Title level={5}>
                  Evaluation Costs by Model
                  {selectedInstructionId && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      Filtered by Instruction
                    </Tag>
                  )}
                  {selectedTestName && (
                    <Tag color="purple" style={{ marginLeft: 8 }}>
                      Filtered by Test Name
                    </Tag>
                  )}
                  {selectedBatchId && (
                    <Tag color="gold" style={{ marginLeft: 8 }}>
                      Filtered by Batch
                    </Tag>
                  )}
                </Title>

                {(() => {
                  if (!selectedLanguage) return null
                  const aggregates = getEvaluationCostAggregates(
                    selectedLanguage,
                    selectedInstructionId,
                    selectedTestName,
                    selectedBatchId
                  )
                  const fmtTokens = (n: number) => (n ?? 0).toLocaleString()
                  const fmtCost = (n: number) => `$${(n ?? 0).toFixed(5)}`
                  const totalEvalCost = aggregates.reduce((sum, a) => sum + (a.costBreakdown?.totalCost ?? 0), 0)
                  const totalTransCost = aggregates.reduce((sum, a) => sum + (a.translationCostBreakdown?.totalCost ?? 0), 0)

                  return (
                    <>
                      <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={6}>
                          <Card>
                            <Statistic title="Total Translation Cost" value={fmtCost(totalTransCost)} />
                          </Card>
                        </Col>
                        <Col span={6}>
                          <Card>
                            <Statistic title="Total Evaluation Cost" value={fmtCost(totalEvalCost)} />
                          </Card>
                        </Col>
                        <Col span={6}>
                          <Card>
                            <Statistic title="Models" value={aggregates.length} />
                          </Card>
                        </Col>
                      </Row>

                      <Table
                        dataSource={aggregates.map(a => ({ key: a.model, ...a }))}
                        columns={[
                          {
                            title: 'Model',
                            dataIndex: 'model',
                            key: 'model',
                            render: (model: string) => <Text strong>{model}</Text>,
                            fixed: 'left',
                          },
                          // Translation tokens
                          {
                            title: 'Trans Input Tokens',
                            key: 'transInputTokens',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtTokens(a.tokenBreakdown.inputTokens),
                            sorter: (a: any, b: any) => a.tokenBreakdown.inputTokens - b.tokenBreakdown.inputTokens,
                          },
                          {
                            title: 'Trans Cached Tokens',
                            key: 'transCachedTokens',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtTokens(a.tokenBreakdown.cachedTokens),
                            sorter: (a: any, b: any) => a.tokenBreakdown.cachedTokens - b.tokenBreakdown.cachedTokens,
                          },
                          {
                            title: 'Trans Output Tokens',
                            key: 'transOutputTokens',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtTokens(a.tokenBreakdown.outputTokens),
                            sorter: (a: any, b: any) => a.tokenBreakdown.outputTokens - b.tokenBreakdown.outputTokens,
                          },
                          {
                            title: 'Trans Reasoning Tokens',
                            key: 'transReasoningTokens',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtTokens(a.tokenBreakdown.reasoningTokens),
                            sorter: (a: any, b: any) => a.tokenBreakdown.reasoningTokens - b.tokenBreakdown.reasoningTokens,
                          },
                          // Translation costs
                          {
                            title: 'Trans Input Cost',
                            key: 'transInputCost',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtCost(a.translationCostBreakdown.inputCost),
                            sorter: (a: any, b: any) => a.translationCostBreakdown.inputCost - b.translationCostBreakdown.inputCost,
                          },
                          {
                            title: 'Trans Cached Cost',
                            key: 'transCachedCost',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtCost(a.translationCostBreakdown.cachedCost),
                            sorter: (a: any, b: any) => a.translationCostBreakdown.cachedCost - b.translationCostBreakdown.cachedCost,
                          },
                          {
                            title: 'Trans Output Cost',
                            key: 'transOutputCost',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtCost(a.translationCostBreakdown.outputCost),
                            sorter: (a: any, b: any) => a.translationCostBreakdown.outputCost - b.translationCostBreakdown.outputCost,
                          },
                          {
                            title: 'Trans Reasoning Cost',
                            key: 'transReasoningCost',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtCost(a.translationCostBreakdown.reasoningCost),
                            sorter: (a: any, b: any) => a.translationCostBreakdown.reasoningCost - b.translationCostBreakdown.reasoningCost,
                          },
                          {
                            title: 'Trans Total Cost',
                            key: 'transTotalCost',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtCost(a.translationCostBreakdown.totalCost),
                            sorter: (a: any, b: any) => a.translationCostBreakdown.totalCost - b.translationCostBreakdown.totalCost,
                          },
                          // Evaluation costs
                          {
                            title: 'Eval Input Cost',
                            key: 'evalInputCost',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtCost(a.costBreakdown.inputCost),
                            sorter: (a: any, b: any) => a.costBreakdown.inputCost - b.costBreakdown.inputCost,
                          },
                          {
                            title: 'Eval Cached Cost',
                            key: 'evalCachedCost',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtCost(a.costBreakdown.cachedCost),
                            sorter: (a: any, b: any) => a.costBreakdown.cachedCost - b.costBreakdown.cachedCost,
                          },
                          {
                            title: 'Eval Output Cost',
                            key: 'evalOutputCost',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtCost(a.costBreakdown.outputCost),
                            sorter: (a: any, b: any) => a.costBreakdown.outputCost - b.costBreakdown.outputCost,
                          },
                          {
                            title: 'Eval Reasoning Cost',
                            key: 'evalReasoningCost',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtCost(a.costBreakdown.reasoningCost),
                            sorter: (a: any, b: any) => a.costBreakdown.reasoningCost - b.costBreakdown.reasoningCost,
                          },
                          {
                            title: 'Eval Total Cost',
                            key: 'evalTotalCost',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtCost(a.costBreakdown.totalCost),
                            sorter: (a: any, b: any) => a.costBreakdown.totalCost - b.costBreakdown.totalCost,
                          },
                          {
                            title: 'All-in Total Cost',
                            key: 'allInTotalCost',
                            align: 'right' as const,
                            render: (_: any, a: EvalCostAggregate) => fmtCost((a.translationCostBreakdown?.totalCost ?? 0) + (a.costBreakdown?.totalCost ?? 0)),
                            sorter: (a: any, b: any) => ((a.translationCostBreakdown?.totalCost ?? 0) + (a.costBreakdown?.totalCost ?? 0)) - ((b.translationCostBreakdown?.totalCost ?? 0) + (b.costBreakdown?.totalCost ?? 0)),
                            defaultSortOrder: 'descend',
                          },
                        ]}
                        pagination={{ pageSize: 10 }}
                        summary={(pageData) => {
                          const data = (pageData || []) as readonly EvalCostAggregate[]
                          const sums = (data as EvalCostAggregate[]).reduce((acc, a) => {
                            acc.transIn += a.tokenBreakdown?.inputTokens ?? 0
                            acc.transCached += a.tokenBreakdown?.cachedTokens ?? 0
                            acc.transOut += a.tokenBreakdown?.outputTokens ?? 0
                            acc.transReason += a.tokenBreakdown?.reasoningTokens ?? 0

                            acc.transInputCost += a.translationCostBreakdown?.inputCost ?? 0
                            acc.transCachedCost += a.translationCostBreakdown?.cachedCost ?? 0
                            acc.transOutputCost += a.translationCostBreakdown?.outputCost ?? 0
                            acc.transReasonCost += a.translationCostBreakdown?.reasoningCost ?? 0
                            acc.transTotalCost += a.translationCostBreakdown?.totalCost ?? 0

                            acc.evalInputCost += a.costBreakdown?.inputCost ?? 0
                            acc.evalCachedCost += a.costBreakdown?.cachedCost ?? 0
                            acc.evalOutputCost += a.costBreakdown?.outputCost ?? 0
                            acc.evalReasonCost += a.costBreakdown?.reasoningCost ?? 0
                            acc.evalTotalCost += a.costBreakdown?.totalCost ?? 0

                            acc.allIn += (a.translationCostBreakdown?.totalCost ?? 0) + (a.costBreakdown?.totalCost ?? 0)
                            return acc
                          }, {
                            transIn: 0, transCached: 0, transOut: 0, transReason: 0,
                            transInputCost: 0, transCachedCost: 0, transOutputCost: 0, transReasonCost: 0, transTotalCost: 0,
                            evalInputCost: 0, evalCachedCost: 0, evalOutputCost: 0, evalReasonCost: 0, evalTotalCost: 0,
                            allIn: 0
                          } as any)

                          return (
                            <Table.Summary fixed>
                              <Table.Summary.Row>
                                <Table.Summary.Cell index={0} />
                                <Table.Summary.Cell index={1}><strong>Totals</strong></Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align="right">{fmtTokens(sums.transIn)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={3} align="right">{fmtTokens(sums.transCached)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={4} align="right">{fmtTokens(sums.transOut)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={5} align="right">{fmtTokens(sums.transReason)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={6} align="right">{fmtCost(sums.transInputCost)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={7} align="right">{fmtCost(sums.transCachedCost)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={8} align="right">{fmtCost(sums.transOutputCost)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={9} align="right">{fmtCost(sums.transReasonCost)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={10} align="right"><strong>{fmtCost(sums.transTotalCost)}</strong></Table.Summary.Cell>
                                <Table.Summary.Cell index={11} align="right">{fmtCost(sums.evalInputCost)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={12} align="right">{fmtCost(sums.evalCachedCost)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={13} align="right">{fmtCost(sums.evalOutputCost)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={14} align="right">{fmtCost(sums.evalReasonCost)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={15} align="right"><strong>{fmtCost(sums.evalTotalCost)}</strong></Table.Summary.Cell>
                                <Table.Summary.Cell index={16} align="right"><strong>{fmtCost(sums.allIn)}</strong></Table.Summary.Cell>
                              </Table.Summary.Row>
                            </Table.Summary>
                          )
                        }}
                        size="small"
                        bordered
                        expandable={{
                          expandedRowRender: (record: EvalCostAggregate) => {
                            const entries = Object.entries(record.evaluators || {})
                            if (!entries.length) return <Text type="secondary">No evaluator breakdowns</Text>
                            return (
                              <Table
                                dataSource={entries.map(([evalModel, data]) => ({
                                  key: evalModel,
                                  evalModel,
                                  ...data,
                                }))}
                                columns={[
                                  { title: 'Evaluator', dataIndex: 'evalModel', key: 'evalModel' },
                                  {
                                    title: 'Input Tokens',
                                    key: 'inputTokens',
                                    align: 'right' as const,
                                    render: (_: any, r: any) => fmtTokens(r.tokenBreakdown.inputTokens),
                                  },
                                  {
                                    title: 'Cached Tokens',
                                    key: 'cachedTokens',
                                    align: 'right' as const,
                                    render: (_: any, r: any) => fmtTokens(r.tokenBreakdown.cachedTokens),
                                  },
                                  {
                                    title: 'Output Tokens',
                                    key: 'outputTokens',
                                    align: 'right' as const,
                                    render: (_: any, r: any) => fmtTokens(r.tokenBreakdown.outputTokens),
                                  },
                                  {
                                    title: 'Reasoning Tokens',
                                    key: 'reasoningTokens',
                                    align: 'right' as const,
                                    render: (_: any, r: any) => fmtTokens(r.tokenBreakdown.reasoningTokens),
                                  },
                                  {
                                    title: 'Input Cost',
                                    key: 'inputCost',
                                    align: 'right' as const,
                                    render: (_: any, r: any) => fmtCost(r.costBreakdown.inputCost),
                                  },
                                  {
                                    title: 'Cached Cost',
                                    key: 'cachedCost',
                                    align: 'right' as const,
                                    render: (_: any, r: any) => fmtCost(r.costBreakdown.cachedCost),
                                  },
                                  {
                                    title: 'Output Cost',
                                    key: 'outputCost',
                                    align: 'right' as const,
                                    render: (_: any, r: any) => fmtCost(r.costBreakdown.outputCost),
                                  },
                                  {
                                    title: 'Reasoning Cost',
                                    key: 'reasoningCost',
                                    align: 'right' as const,
                                    render: (_: any, r: any) => fmtCost(r.costBreakdown.reasoningCost),
                                  },
                                  {
                                    title: 'Total Cost',
                                    key: 'totalCost',
                                    align: 'right' as const,
                                    render: (_: any, r: any) => fmtCost(r.costBreakdown.totalCost),
                                  },
                                ]}
                                pagination={false}
                                size="small"
                                bordered
                              />
                            )
                          },
                        }}
                        scroll={{ x: 'max-content' }}
                      />
                    </>
                  )
                })()}
              </Tabs.TabPane>

              <Tabs.TabPane
                key="rankings"
                tab={
                  <Space>
                    <CrownOutlined />
                    Model Rankings
                  </Space>
                }
              >
                <Title level={5}>
                  Models Ranked by Average Overall Score
                  {selectedInstructionId && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      Filtered by Instruction
                    </Tag>
                  )}
                  {selectedTestName && (
                    <Tag color="purple" style={{ marginLeft: 8 }}>
                      Filtered by Test Name
                    </Tag>
                  )}
                  {selectedBatchId && (
                    <Tag color="gold" style={{ marginLeft: 8 }}>
                      Filtered by Batch
                    </Tag>
                  )}
                </Title>

                {(() => {
                  const modelRankings = getModelRankings(
                    selectedLanguage,
                    selectedInstructionId,
                    selectedTestName,
                    selectedBatchId
                  )

                  return (
                    <>
                      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                        {modelRankings.slice(0, 3).map((model, index) => (
                          <Col span={8} key={model.model}>
                            <Badge.Ribbon
                              text={`#${index + 1}`}
                              color={index === 0 ? 'gold' : index === 1 ? 'silver' : '#cd7f32'}
                            >
                              <Card>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                  <Title level={4} style={{ margin: 0 }}>
                                    {model.model}
                                  </Title>
                                  <Row gutter={16}>
                                    <Col span={12}>
                                      <Statistic
                                        title="Overall Score"
                                        value={model.overallScore.toFixed(2)}
                                        valueStyle={{ color: getScoreColor(model.overallScore), fontSize: 24 }}
                                      />
                                    </Col>
                                    <Col span={12}>
                                      <Statistic
                                        title="Avg Cost"
                                        value={model.avgCost.toFixed(4)}
                                        prefix="$"
                                        valueStyle={{ fontSize: 18 }}
                                      />
                                    </Col>
                                  </Row>
                                  <Row gutter={16}>
                                    <Col span={12}>
                                      <Statistic title="Tests" value={model.totalTests} valueStyle={{ fontSize: 16 }} />
                                    </Col>
                                    <Col span={12}>
                                      <Statistic
                                        title="Cost/Point"
                                        value={model.costPerPoint === Infinity ? 'N/A' : model.costPerPoint.toFixed(4)}
                                        prefix={model.costPerPoint !== Infinity ? '$' : ''}
                                        valueStyle={{ fontSize: 16 }}
                                      />
                                    </Col>
                                  </Row>
                                  <div>
                                    <Progress
                                      percent={Math.min(model.efficiency * 10, 100)}
                                      strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                                      format={() => `Efficiency: ${model.efficiency.toFixed(1)}`}
                                    />
                                  </div>
                                </Space>
                              </Card>
                            </Badge.Ribbon>
                          </Col>
                        ))}
                      </Row>

                      <Table
                        dataSource={modelRankings.map((model, index) => ({ ...model, rank: index + 1 }))}
                        columns={[
                          {
                            title: 'Rank',
                            dataIndex: 'rank',
                            key: 'rank',
                            width: 80,
                            render: (rank: number) => (
                              <Tag color={rank <= 3 ? 'gold' : rank <= 5 ? 'blue' : 'default'}>#{rank}</Tag>
                            ),
                          },
                          {
                            title: 'Model',
                            dataIndex: 'model',
                            key: 'model',
                            width: 150,
                            render: (model: string) => <Text strong>{model}</Text>,
                          },
                          {
                            title: 'Overall Score',
                            dataIndex: 'overallScore',
                            key: 'overallScore',
                            width: 120,
                            align: 'center' as const,
                            render: (score: number) => (
                              <Tag color={getScoreColor(score)} style={{ fontSize: 14 }}>
                                {score.toFixed(2)}
                              </Tag>
                            ),
                            sorter: (a: any, b: any) => b.overallScore - a.overallScore,
                            defaultSortOrder: 'descend',
                          },
                          {
                            title: 'Average Cost',
                            dataIndex: 'avgCost',
                            key: 'avgCost',
                            width: 120,
                            align: 'right' as const,
                            render: (cost: number) => (cost > 0 ? `$${cost.toFixed(5)}` : 'N/A'),
                            sorter: (a: any, b: any) => a.avgCost - b.avgCost,
                          },
                          {
                            title: 'Cost per Point',
                            dataIndex: 'costPerPoint',
                            key: 'costPerPoint',
                            width: 130,
                            align: 'right' as const,
                            render: (cpp: number) => (cpp === Infinity ? 'N/A' : `$${cpp.toFixed(5)}`),
                            sorter: (a: any, b: any) => {
                              if (a.costPerPoint === Infinity) return 1
                              if (b.costPerPoint === Infinity) return -1
                              return a.costPerPoint - b.costPerPoint
                            },
                          },
                          {
                            title: 'Efficiency',
                            dataIndex: 'efficiency',
                            key: 'efficiency',
                            width: 120,
                            align: 'center' as const,
                            render: (efficiency: number) => (
                              <Progress
                                percent={Math.min(efficiency * 10, 100)}
                                size="small"
                                format={() => efficiency.toFixed(1)}
                                strokeColor={{ '0%': '#ff4d4f', '50%': '#faad14', '100%': '#52c41a' }}
                              />
                            ),
                            sorter: (a: any, b: any) => b.efficiency - a.efficiency,
                          },
                          {
                            title: 'Tests',
                            dataIndex: 'totalTests',
                            key: 'totalTests',
                            width: 80,
                            align: 'center' as const,
                            sorter: (a: any, b: any) => b.totalTests - a.totalTests,
                          },
                          {
                            title: 'Category Breakdown',
                            key: 'categories',
                            render: (_: any, record: ModelRanking) => (
                              <Tooltip
                                title={
                                  <div>
                                    {Object.entries(record.categoryBreakdown).map(([category, data]) => (
                                      <div key={category} style={{ marginBottom: 4 }}>
                                        <strong>{category}:</strong> {data.average.toFixed(2)} ({data.count} tests with valid scores)
                                      </div>
                                    ))}
                                  </div>
                                }
                              >
                                <Button size="small" icon={<InfoCircleOutlined />}>
                                  View Details
                                </Button>
                              </Tooltip>
                            ),
                          },
                        ]}
                        pagination={{ pageSize: 10 }}
                        size="small"
                        scroll={{ x: 'max-content' }}
                      />
                    </>
                  )
                })()}
              </Tabs.TabPane>
            </Tabs>
              </>
            )}
            </>
          </Spin>
        )}
      </Space>
    </Modal>
  )
}

export default AggregateResultsModal
